import React from 'react';
import { Space } from 'antd';
import { useNavigate, useParams } from 'react-router';
import styled from '@emotion/styled';
import Header from './components/Header';
import InspectionRequirement from './components/InspectionRequirement';
import OtherDescription from './components/OtherDescription';
import {
  useCancelInspectionMutation,
  useLazyGetInspectionQuery
} from 'app/features/inspection/api';
import pick from 'lodash/pick';
import get from 'lodash/get';
import Tabs from 'app/components/Tabs';
import Table from 'app/components/Table';
import Reports from './components/Tabs/Reports';
import AttachmentTab from './components/Tabs/Attachments';
import Modal from 'app/components/Modal';
import { debounce, isEmpty } from 'lodash';
import useFetchInspectionProgress from '../ReportDetail/hooks/useFetchInspectionProgress';

const Content = styled.div`
  padding: 35px 40px;

  > div:first-of-type {
    width: 100%;
    > div {
      flex: 0.5;
      height: 400px;
      overflow: auto;
    }
  }
`;
const StyledTable = styled(Table)`
  .ant-table-empty {
    .ant-table-cell {
      border-bottom: 0;
    }
  }
`;
const JobDetail = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [fetch, result] = useLazyGetInspectionQuery();
  const [cancelInspection] = useCancelInspectionMutation();
  const { data: ProgressData } = useFetchInspectionProgress(
    params.id as string
  );

  const status = React.useMemo(() => {
    return get(ProgressData?.data, 'overallResult');
  }, [ProgressData]);
  const onBookInspection = () => {
    navigate(`/dashboard/inspection/book-inspection/${params.id}`);
  };

  const products = React.useMemo(() => {
    return get(result.data, 'inspectionPOReportGroup.inspectionPOItems', []);
  }, [result]);

  const suppliers = React.useMemo(() => {
    if (isEmpty(result.data)) return [];
    const inspectionSuppliers = get(result.data, 'inspectionSuppliers', []);
    return inspectionSuppliers;
  }, [result]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refetch = React.useCallback(
    debounce(() => {
      fetch(params.id);
    }, 250),
    []
  );
  const onCancelInspection = () => {
    Modal.danger({
      title: 'Confirm to cancel the inspection job?',
      onOk: async () => {
        try {
          await cancelInspection(params.id as string).unwrap();
          refetch();
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  React.useEffect(() => {
    fetch(params.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div>
      <Header
        status={result.data?.status}
        jobId={result.data?.jobNoList.jobNo}
        onBookInspection={onBookInspection}
        overallReportResult={status || 'pending'}
        onActionClick={({ key }) => {
          if (key === 'edit-inspection-detail') {
            onBookInspection();
            return;
          } else {
            //Show Cancel Modal
            onCancelInspection();
          }
        }}
        onBack={() => {
          navigate('/dashboard');
        }}
      ></Header>
      <Content>
        <Space wrap align="start" direction="horizontal" size={16}>
          <InspectionRequirement
            loading={result.isLoading}
            data={pick(result.data, [
              'generalInspectLevel',
              'inspectionDate',
              'inspectionEndDate',
              'noOfManday',
              'typeOfService',
              'remark',
              'criticalLevel',
              'criticalMajorLevel',
              'criticalMinorLevel',
              'assignTo',
              'rejectReasons',
              'checkinTime',
              'checkoutTime'
            ])}
          ></InspectionRequirement>
          <OtherDescription
            factory={get(
              pick(result.data, ['inspectionFactory']),
              'inspectionFactory',
              {}
            )}
            suppliers={suppliers}
            products={products}
          ></OtherDescription>
        </Space>
        <Tabs
          items={[
            {
              key: 'report',
              label: 'Report',
              children: (
                <Reports
                  reportId={result?.data?.id}
                  status={result.data?.status}
                  poItems={
                    result?.data?.inspectionPOReportGroup?.inspectionPOItems ??
                    []
                  }
                ></Reports>
              )
            },
            {
              key: 'attachment',
              label: 'Attachment',
              children: (
                <AttachmentTab inspectionId={result?.data?.id}></AttachmentTab>
              )
            },
            {
              key: 'comment',
              label: 'Comment',
              children: <StyledTable></StyledTable>
            }
          ]}
        ></Tabs>
      </Content>
    </div>
  );
};

export default JobDetail;
